import { useState } from "react";
import { CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../../i18n/LanguageContext";
import { api } from "../../lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const SOFTWARES = ["Tally", "Busy", "Marg ERP", "Zoho Books", "Other"];

export const InquiryForm = () => {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "",
    shop_name: "",
    phone: "",
    email: "",
    software: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handle = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.software) {
      toast.error(t.inquiry.softwarePlaceholder);
      return;
    }
    setSubmitting(true);
    try {
      const details = [
        `Mobile / Phone: ${form.phone}`,
        `Shop Name: ${form.shop_name}`,
        `Accounting Software: ${form.software}`,
        form.message?.trim() ? `Message: ${form.message.trim()}` : ""
      ].filter(Boolean).join("\n");

      const params = new URLSearchParams();
      params.append("name", form.name || "");
      params.append("email", form.email || "");
      params.append("phone", form.phone || "");
      params.append("mobile", form.phone || "");
      params.append("contact", form.phone || "");
      params.append("shop_name", form.shop_name || "");
      params.append("software", form.software || "");
      params.append("message", details);

      const res = await api.post("/inquiries.php", params, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      if (res.status === 200 || res.status === 201 || res.data?.success) {
        setSubmitted(true);
        toast.success(t.inquiry.success);
        setForm({
          name: "",
          shop_name: "",
          phone: "",
          email: "",
          software: "",
          message: "",
        });
      } else {
        toast.error(res.data?.error || t.inquiry.error);
      }
    } catch (err) {
      console.error("Inquiry form submission error:", err);
      const serverMsg = err?.response?.data?.error || err?.message || t.inquiry.error;
      toast.error(serverMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="inquiry-section"
      className="py-20 lg:py-28 border-t border-stone-200"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <span className="font-mono-label text-stone-500">
            {t.inquiry.eyebrow}
          </span>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl tracking-tight text-stone-900 leading-tight">
            {t.inquiry.title}
          </h2>
          <p className="mt-5 text-lg text-stone-600 leading-relaxed">
            {t.inquiry.subtitle}
          </p>
        </div>

        <div className="lg:col-span-3">
          {submitted ? (
            <div
              data-testid="inquiry-success"
              className="bg-green-900 text-stone-50 rounded-2xl p-10 flex flex-col items-center text-center"
            >
              <CheckCircle2 size={48} className="text-stone-50" />
              <h3 className="font-display text-2xl mt-4">{t.inquiry.success}</h3>
              <button
                onClick={() => setSubmitted(false)}
                data-testid="submit-another-btn"
                className="mt-6 px-5 py-2.5 rounded-md bg-stone-50 text-green-900 font-semibold hover:bg-white transition"
              >
                Submit another
              </button>
            </div>
          ) : (
            <form
              data-testid="inquiry-form"
              onSubmit={submit}
              className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-5"
            >
              <Field label={t.inquiry.name} required id="inquiry-name">
                <input
                  id="inquiry-name"
                  name="name"
                  autoComplete="name"
                  required
                  data-testid="inquiry-name"
                  value={form.name}
                  onChange={handle("name")}
                  className={inputCls}
                />
              </Field>
              <Field label={t.inquiry.shopName} required id="inquiry-shop-name">
                <input
                  id="inquiry-shop-name"
                  name="shop_name"
                  autoComplete="organization"
                  required
                  data-testid="inquiry-shop-name"
                  value={form.shop_name}
                  onChange={handle("shop_name")}
                  className={inputCls}
                />
              </Field>
              <Field label={t.inquiry.phone} required id="inquiry-phone">
                <input
                  id="inquiry-phone"
                  name="phone"
                  autoComplete="tel"
                  required
                  type="tel"
                  data-testid="inquiry-phone"
                  value={form.phone}
                  onChange={handle("phone")}
                  className={inputCls}
                />
              </Field>
              <Field label={t.inquiry.email} required id="inquiry-email">
                <input
                  id="inquiry-email"
                  name="email"
                  autoComplete="email"
                  required
                  type="email"
                  data-testid="inquiry-email"
                  value={form.email}
                  onChange={handle("email")}
                  className={inputCls}
                />
              </Field>
              <div className="sm:col-span-2">
                <label htmlFor="inquiry-software" id="software-label" className="font-mono-label text-stone-500 mb-2 block">
                  {t.inquiry.software} <span className="text-red-800">*</span>
                </label>
                <Select
                  name="software"
                  value={form.software}
                  onValueChange={(v) => setForm((f) => ({ ...f, software: v }))}
                >
                  <SelectTrigger
                    id="inquiry-software"
                    aria-labelledby="software-label"
                    data-testid="inquiry-software"
                    className="bg-stone-50 border-stone-300 h-12 rounded-md"
                  >
                    <SelectValue placeholder={t.inquiry.softwarePlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {SOFTWARES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`software-${s}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="inquiry-message" className="font-mono-label text-stone-500 mb-2 block">
                  {t.inquiry.message}
                </label>
                <textarea
                  id="inquiry-message"
                  name="message"
                  rows={4}
                  data-testid="inquiry-message"
                  placeholder={t.inquiry.messagePlaceholder}
                  value={form.message}
                  onChange={handle("message")}
                  className={`${inputCls} resize-none`}
                />
              </div>

              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="inquiry-submit-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-green-900 text-stone-50 font-semibold hover:bg-green-800 transition-colors disabled:opacity-60"
                >
                  {submitting ? t.inquiry.submitting : t.inquiry.submit}
                  {!submitting && <Send size={16} />}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

const inputCls =
  "w-full h-12 px-4 rounded-md bg-stone-50 border border-stone-300 text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-green-900 focus:border-green-900 transition";

const Field = ({ label, required, id, children }) => (
  <div>
    <label htmlFor={id} className="font-mono-label text-stone-500 mb-2 block">
      {label} {required && <span className="text-red-800">*</span>}
    </label>
    {children}
  </div>
);
